import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  Upload, Scale, ChevronRight, ChevronLeft, Check,
  AlertTriangle, X, Copy, ArrowLeft, CheckCircle, XCircle
} from 'lucide-react'
import Papa from 'papaparse'
import { getCattle, importWeighSession } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'

// Step indicator component
function StepIndicator({ currentStep }) {
  const steps = ['Upload CSV', 'Preview & Configure', 'Results']
  return (
    <div className="flex items-center justify-center mb-8">
      {steps.map((label, i) => (
        <div key={label} className="flex items-center">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
            i < currentStep
              ? 'bg-green-100 text-green-700'
              : i === currentStep
                ? 'bg-primary-100 text-primary-700'
                : 'bg-gray-100 text-gray-500'
          }`}>
            {i < currentStep ? (
              <Check className="h-4 w-4" />
            ) : (
              <span className="h-5 w-5 rounded-full bg-current/20 flex items-center justify-center text-xs">
                {i + 1}
              </span>
            )}
            {label}
          </div>
          {i < steps.length - 1 && (
            <ChevronRight className="h-5 w-5 mx-2 text-gray-400" />
          )}
        </div>
      ))}
    </div>
  )
}

// Flexible CSV header matching
function resolveHeaders(headers) {
  const lower = headers.map(h => h.toLowerCase().trim())
  const find = (...candidates) => {
    for (const c of candidates) {
      const idx = lower.findIndex(h => h === c || h.includes(c))
      if (idx >= 0) return headers[idx]
    }
    return null
  }
  return {
    eid: find('eid', 'rfid', 'electronic id', 'e.i.d'),
    vid: find('vid', 'visual id', 'tag', 'visual'),
    weight: find('weight', 'wt', 'mass', 'live weight'),
    date: find('date', 'weigh date'),
    time: find('time', 'weigh time'),
  }
}

// Tru-Test (and most readers) emit EIDs with an embedded space, e.g.
// "982 091064804958". The stored rfid_tag is whitespace-free (migration 100),
// so strip ALL whitespace before matching/sending — .trim() alone leaves the
// internal space and every row shows "No match".
function normalizeRfid(value) {
  return (value ?? '').toString().replace(/\s+/g, '')
}

function parseCSVDate(dateStr) {
  if (!dateStr) return new Date().toISOString().split('T')[0]
  // Try common formats: DD/MM/YYYY, YYYY-MM-DD, MM/DD/YYYY
  const parts = dateStr.split(/[/\-.]/)
  if (parts.length === 3) {
    const [a, b, c] = parts
    if (a.length === 4) return `${a}-${b.padStart(2, '0')}-${c.padStart(2, '0')}` // YYYY-MM-DD
    if (parseInt(c) > 31) return `${c}-${b.padStart(2, '0')}-${a.padStart(2, '0')}` // DD/MM/YYYY
  }
  return dateStr
}

export default function WeighSessionImport({ ranchId }) {
  const navigate = useNavigate()
  const { userId } = useAuth()
  const [step, setStep] = useState(0)
  const [error, setError] = useState('')

  // Step 1 state
  const [sessionDate, setSessionDate] = useState(new Date().toISOString().split('T')[0])
  const [sourceDevice, setSourceDevice] = useState('Tru-Test XR5000')
  const [fileName, setFileName] = useState('')
  const [parsedRows, setParsedRows] = useState([])
  const [parseErrors, setParseErrors] = useState([])

  // Step 2 state
  const [animals, setAnimals] = useState([]) // enriched with match info
  const [showSales, setShowSales] = useState(false)
  const [pricePerKg, setPricePerKg] = useState('')
  const [globalBuyer, setGlobalBuyer] = useState({ name: '', contact: '', payment_method: '' })
  const [notes, setNotes] = useState('')

  // Step 3 state
  const [importResult, setImportResult] = useState(null)

  // Load the FULL herd for RFID matching. The list endpoint caps page size at
  // 500, so paginate — a ranch with >500 animals (Muyogoma has ~1,500) would
  // otherwise leave most tags unmatchable in the preview (and unsent on import,
  // since only matched rows are submitted).
  const { data: cattleData } = useQuery({
    queryKey: ['cattle', ranchId, 'all-for-match'],
    enabled: !!ranchId,
    staleTime: 60000,
    queryFn: async () => {
      const PAGE = 500
      const items = []
      for (let offset = 0; ; offset += PAGE) {
        const res = await getCattle(ranchId, { limit: PAGE, offset, status: '' })
        const page = res?.items || []
        items.push(...page)
        if (page.length < PAGE) break
      }
      return { items }
    },
  })

  const cattleByRfid = useMemo(() => {
    const map = {}
    const items = cattleData?.items || []
    for (const a of items) {
      if (a.rfid_tag) map[normalizeRfid(a.rfid_tag)] = a
    }
    return map
  }, [cattleData])

  // Import mutation
  const importMutation = useMutation({
    mutationFn: importWeighSession,
    onSuccess: (data) => {
      setImportResult(data)
      setStep(2)
    },
    onError: (err) => {
      setError(err.response?.data?.detail || 'Import failed')
    }
  })

  // --- Step 1: Upload CSV ---
  const handleFileUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return
    setError('')
    setFileName(file.name)

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        if (result.errors.length > 0) {
          setParseErrors(result.errors.slice(0, 5))
        }
        if (result.data.length === 0) {
          setError('No data rows found in CSV')
          return
        }
        const headers = resolveHeaders(Object.keys(result.data[0]))
        if (!headers.eid && !headers.vid) {
          setError('Could not find EID/RFID or VID column in CSV headers. Found: ' + Object.keys(result.data[0]).join(', '))
          return
        }
        if (!headers.weight) {
          setError('Could not find Weight column in CSV headers. Found: ' + Object.keys(result.data[0]).join(', '))
          return
        }

        const rows = result.data
          .map(row => ({
            rfid_tag: headers.eid ? normalizeRfid(row[headers.eid]) : '',
            vid: headers.vid ? row[headers.vid]?.trim() : '',
            weight_kg: parseFloat(row[headers.weight]) || 0,
            weigh_date: headers.date ? parseCSVDate(row[headers.date]?.trim()) : sessionDate,
            weigh_time: headers.time ? row[headers.time]?.trim() : null,
          }))
          .filter(r => r.rfid_tag && r.weight_kg > 0)

        if (rows.length === 0) {
          setError('No valid rows with RFID and weight found')
          return
        }

        setParsedRows(rows)
        // Default the session date to the date carried in the file rather than
        // today, so the session metadata matches the per-row weigh dates and
        // can't silently drift (a Tru-Test session is a single weighing day).
        // The user can still override it on the next step.
        const fileDates = rows.map(r => r.weigh_date).filter(Boolean)
        if (headers.date && fileDates.length) {
          const freq = {}
          for (const d of fileDates) freq[d] = (freq[d] || 0) + 1
          const dominant = Object.entries(freq).sort((a, b) => b[1] - a[1])[0][0]
          setSessionDate(dominant)
        }
        setParseErrors(result.errors.slice(0, 5))
      },
      error: (err) => {
        setError('Failed to parse CSV: ' + err.message)
      }
    })
  }

  const goToPreview = () => {
    if (parsedRows.length === 0) {
      setError('Please upload a CSV file first')
      return
    }
    // Enrich rows with cattle match data
    const enriched = parsedRows.map(row => {
      const match = cattleByRfid[row.rfid_tag]
      return {
        ...row,
        matched: !!match,
        animal_id: match?.id || null,
        animal_name: match?.local_name || null,
        animal_category: match?.category || null,
        animal_status: match?.status || null,
        is_sale: false,
        buyer_name: '',
        buyer_contact: '',
        payment_method: '',
      }
    })
    setAnimals(enriched)
    setStep(1)
    setError('')
  }

  // --- Step 2: Preview & Configure ---
  const matchedCount = animals.filter(a => a.matched).length
  const unmatchedCount = animals.filter(a => !a.matched).length
  const saleCount = animals.filter(a => a.is_sale).length

  const toggleSale = (index) => {
    setAnimals(prev => prev.map((a, i) => i === index ? { ...a, is_sale: !a.is_sale } : a))
  }

  const selectAllSales = () => {
    setAnimals(prev => prev.map(a =>
      a.matched && a.animal_status !== 'sold' ? { ...a, is_sale: true } : a
    ))
  }

  const deselectAllSales = () => {
    setAnimals(prev => prev.map(a => ({ ...a, is_sale: false })))
  }

  const copyBuyerToAll = () => {
    setAnimals(prev => prev.map(a =>
      a.is_sale
        ? { ...a, buyer_name: globalBuyer.name, buyer_contact: globalBuyer.contact, payment_method: globalBuyer.payment_method }
        : a
    ))
  }

  const updateAnimalField = (index, field, value) => {
    setAnimals(prev => prev.map((a, i) => i === index ? { ...a, [field]: value } : a))
  }

  const handleSubmit = () => {
    if (showSales && saleCount > 0 && !pricePerKg) {
      setError('Please enter the price per kg for sales')
      return
    }
    setError('')

    const payload = {
      ranch_id: ranchId,
      session_date: sessionDate,
      source_device: sourceDevice,
      file_name: fileName,
      price_per_kg: showSales && pricePerKg ? parseFloat(pricePerKg) : null,
      imported_by: userId,
      notes: notes || null,
      animals: animals.filter(a => a.matched).map(a => ({
        rfid_tag: a.rfid_tag,
        weight_kg: a.weight_kg,
        weigh_date: a.weigh_date,
        weigh_time: a.weigh_time,
        is_sale: a.is_sale,
        buyer_name: a.buyer_name || null,
        buyer_contact: a.buyer_contact || null,
        payment_method: a.payment_method || null,
      })),
    }

    importMutation.mutate(payload)
  }

  // --- Render ---
  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => step === 0 ? navigate('/cattle') : setStep(step - 1)}
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Import Weigh Session</h1>
          <p className="text-gray-600">Upload Tru-Test CSV to record weights and sales</p>
        </div>
      </div>

      <StepIndicator currentStep={step} />

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Step 1: Upload */}
      {step === 0 && (
        <div className="bg-white rounded-lg shadow p-6 max-w-2xl mx-auto">
          <div className="space-y-6">
            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">CSV File</label>
              <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors">
                <Upload className="h-10 w-10 text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">
                  {fileName ? fileName : 'Click to upload Tru-Test CSV'}
                </span>
                <span className="text-xs text-gray-400 mt-1">Expected: VID, EID, Weight, Date, Time</span>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
            </div>

            {/* Parse summary */}
            {parsedRows.length > 0 && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-green-700 font-medium">
                  <CheckCircle className="h-5 w-5" />
                  {parsedRows.length} animals parsed from CSV
                </div>
                {parseErrors.length > 0 && (
                  <div className="mt-2 text-sm text-amber-700">
                    {parseErrors.length} parse warning(s) — check your CSV format
                  </div>
                )}
              </div>
            )}

            {/* Session Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Session Date</label>
                <input
                  type="date"
                  value={sessionDate}
                  onChange={(e) => setSessionDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source Device</label>
                <input
                  type="text"
                  value={sourceDevice}
                  onChange={(e) => setSourceDevice(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <button
              onClick={goToPreview}
              disabled={parsedRows.length === 0}
              className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
            >
              Preview & Configure
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Preview & Configure */}
      {step === 1 && (
        <div className="space-y-6">
          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Animals</div>
              <div className="text-2xl font-bold">{animals.length}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Matched</div>
              <div className="text-2xl font-bold text-green-600">{matchedCount}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Unmatched</div>
              <div className="text-2xl font-bold text-amber-600">{unmatchedCount}</div>
            </div>
          </div>

          {/* Sale Configuration */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-3 mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showSales}
                  onChange={(e) => {
                    setShowSales(e.target.checked)
                    if (!e.target.checked) deselectAllSales()
                  }}
                  className="h-5 w-5 text-primary-600 rounded"
                />
                <span className="font-medium text-gray-900">Mark sales in this session</span>
              </label>
            </div>

            {showSales && (
              <div className="space-y-4 border-t pt-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price per kg (UGX) *</label>
                    <input
                      type="number"
                      value={pricePerKg}
                      onChange={(e) => setPricePerKg(e.target.value)}
                      placeholder="e.g. 8000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Buyer Name</label>
                    <input
                      type="text"
                      value={globalBuyer.name}
                      onChange={(e) => setGlobalBuyer(b => ({ ...b, name: e.target.value }))}
                      placeholder="Optional"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Buyer Contact</label>
                    <input
                      type="text"
                      value={globalBuyer.contact}
                      onChange={(e) => setGlobalBuyer(b => ({ ...b, contact: e.target.value }))}
                      placeholder="Optional"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                    <select
                      value={globalBuyer.payment_method}
                      onChange={(e) => setGlobalBuyer(b => ({ ...b, payment_method: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Optional</option>
                      <option value="cash">Cash</option>
                      <option value="mobile_money">Mobile Money</option>
                      <option value="bank_transfer">Bank Transfer</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={copyBuyerToAll}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 border border-blue-200"
                  >
                    <Copy className="h-4 w-4" />
                    Copy buyer to all sales
                  </button>
                  <button
                    onClick={selectAllSales}
                    className="px-3 py-1.5 text-sm bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 border border-gray-200"
                  >
                    Select All
                  </button>
                  <button
                    onClick={deselectAllSales}
                    className="px-3 py-1.5 text-sm bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 border border-gray-200"
                  >
                    Deselect All
                  </button>
                  {saleCount > 0 && pricePerKg && (
                    <span className="ml-auto text-sm text-gray-600 self-center">
                      {saleCount} sales = <strong>
                        {animals.filter(a => a.is_sale).reduce((sum, a) => sum + a.weight_kg * parseFloat(pricePerKg || 0), 0).toLocaleString()} UGX
                      </strong>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Animal Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {showSales && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sale</th>}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">RFID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Animal</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Weight (kg)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    {showSales && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Buyer</th>}
                    {showSales && pricePerKg && <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Sale Value</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {animals.map((a, i) => (
                    <tr key={i} className={!a.matched ? 'bg-amber-50' : a.animal_status === 'sold' ? 'bg-gray-50' : ''}>
                      {showSales && (
                        <td className="px-4 py-3">
                          {a.matched && a.animal_status !== 'sold' && (
                            <input
                              type="checkbox"
                              checked={a.is_sale}
                              onChange={() => toggleSale(i)}
                              className="h-4 w-4 text-primary-600 rounded"
                            />
                          )}
                        </td>
                      )}
                      <td className="px-4 py-3 text-sm font-mono text-gray-900">{a.rfid_tag}</td>
                      <td className="px-4 py-3 text-sm">
                        {a.matched ? (
                          <div>
                            <span className="font-medium text-gray-900">{a.animal_name || a.rfid_tag}</span>
                            {a.animal_category && (
                              <span className="ml-2 text-xs text-gray-500">({a.animal_category})</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-amber-600 flex items-center gap-1">
                            <AlertTriangle className="h-4 w-4" />
                            No match
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium">{a.weight_kg}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{a.weigh_date}</td>
                      <td className="px-4 py-3">
                        {!a.matched ? (
                          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">Unmatched</span>
                        ) : a.animal_status === 'sold' ? (
                          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Already Sold</span>
                        ) : (
                          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">Ready</span>
                        )}
                      </td>
                      {showSales && (
                        <td className="px-4 py-3 text-sm">
                          {a.is_sale && (
                            <input
                              type="text"
                              value={a.buyer_name}
                              onChange={(e) => updateAnimalField(i, 'buyer_name', e.target.value)}
                              placeholder="Buyer name"
                              className="w-32 px-2 py-1 text-sm border border-gray-300 rounded"
                            />
                          )}
                        </td>
                      )}
                      {showSales && pricePerKg && (
                        <td className="px-4 py-3 text-sm text-right">
                          {a.is_sale && (
                            <span className="font-medium">
                              {(a.weight_kg * parseFloat(pricePerKg)).toLocaleString()}
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-lg shadow p-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Any notes about this weigh session..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-between">
            <button
              onClick={() => setStep(0)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <ChevronLeft className="h-5 w-5" />
              Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={importMutation.isPending || matchedCount === 0}
              className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {importMutation.isPending ? (
                <>
                  <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                  Importing...
                </>
              ) : (
                <>
                  <Scale className="h-5 w-5" />
                  Import {matchedCount} Animals
                  {saleCount > 0 && ` (${saleCount} sales)`}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 2 && importResult && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Processed</div>
              <div className="text-2xl font-bold">{importResult.total_animals}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Weighed</div>
              <div className="text-2xl font-bold text-green-600">{importResult.matched_animals}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Sold</div>
              <div className="text-2xl font-bold text-blue-600">{importResult.sales_count}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Sale Value</div>
              <div className="text-2xl font-bold text-primary-600">
                {importResult.total_sale_value?.toLocaleString()} UGX
              </div>
            </div>
          </div>

          {importResult.unmatched_animals > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700">
              <AlertTriangle className="inline h-5 w-5 mr-2" />
              {importResult.unmatched_animals} animal(s) could not be matched to an RFID in the system and were skipped.
            </div>
          )}

          {/* Results Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">RFID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Animal</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Weight</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Sale</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {importResult.results.map((r, i) => (
                    <tr key={i} className={
                      r.status === 'unmatched' ? 'bg-amber-50' :
                      r.status === 'error' ? 'bg-red-50' :
                      r.status === 'already_sold' ? 'bg-gray-50' : ''
                    }>
                      <td className="px-4 py-3">
                        {r.status === 'weighed' && <CheckCircle className="h-5 w-5 text-green-500" />}
                        {r.status === 'sold' && <CheckCircle className="h-5 w-5 text-blue-500" />}
                        {r.status === 'unmatched' && <AlertTriangle className="h-5 w-5 text-amber-500" />}
                        {r.status === 'already_sold' && <AlertTriangle className="h-5 w-5 text-gray-400" />}
                        {r.status === 'error' && <XCircle className="h-5 w-5 text-red-500" />}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono">{r.rfid_tag}</td>
                      <td className="px-4 py-3 text-sm font-medium">{r.animal_name || '—'}</td>
                      <td className="px-4 py-3 text-sm text-right">{r.weight_kg} kg</td>
                      <td className="px-4 py-3 text-sm text-right">
                        {r.sale_amount ? `${r.sale_amount.toLocaleString()} UGX` : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{r.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-center">
            <button
              onClick={() => navigate('/cattle')}
              className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Cattle
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
